export type CountryCode = 'NL' | 'BE';

export type ShippingRate = {
  id: string;
  country: CountryCode;
  label: string;
  condition: string;
  customerPrice: number | null;
  carrierCostBasis: number | null;
  currency: 'EUR';
  checkoutUse: string;
  sourceNote: string;
};

export type ReturnCostRate = {
  id: string;
  origin: CountryCode;
  destination: 'Tilburg NL';
  label: string;
  customerCost: number | null;
  currency: 'EUR';
  responsibility: string;
  sourceNote: string;
};

export const shippingPolicyDecision = {
  carrier: 'PostNL',
  sourceCheckedAt: '2026-07-03',
  customerTactic: 'flat standard contribution below threshold, free shipping above threshold, bulky and club packages quote-first',
  checkoutReady: false,
  nextTariffReview: '2026-07-12',
  notes: [
    'PostNL 2026 consumer base rates are used as the current cost basis.',
    'Customer-facing thresholds copy the common NL/BE webshop tactic: visible shipping fee below threshold and free shipping above threshold.',
    'No carrier contract, delivery promise, stock promise or live checkout is claimed.'
  ]
} as const;

export const carrierCostBasisRates: ShippingRate[] = [
  {
    id: 'postnl-nl-letterbox-plus',
    country: 'NL',
    label: 'PostNL Brievenbuspakje+',
    condition: 't/m 2 kg, brievenbusformaat',
    customerPrice: null,
    carrierCostBasis: 4.4,
    currency: 'EUR',
    checkoutUse: 'Cost basis for small add-ons only; avoid standalone low-margin orders.',
    sourceNote: 'PostNL basistarieven binnenland 2026, online frankering.'
  },
  {
    id: 'postnl-nl-small-home',
    country: 'NL',
    label: 'PostNL klein pakket naar huisadres',
    condition: 't/m 3 kg',
    customerPrice: null,
    carrierCostBasis: 6.35,
    currency: 'EUR',
    checkoutUse: 'Cost basis for compact accessories when dimensions fit small package rules.',
    sourceNote: 'PostNL basistarieven binnenland 2026, online frankering.'
  },
  {
    id: 'postnl-nl-standard-home-10kg',
    country: 'NL',
    label: 'PostNL pakket naar huisadres',
    condition: 't/m 10 kg',
    customerPrice: null,
    carrierCostBasis: 7.45,
    currency: 'EUR',
    checkoutUse: 'Default cost basis for paddles, balls, bags and startersets.',
    sourceNote: 'PostNL basistarieven binnenland 2026, online frankering.'
  },
  {
    id: 'postnl-nl-standard-home-23kg',
    country: 'NL',
    label: 'PostNL pakket naar huisadres',
    condition: '10-23 kg',
    customerPrice: null,
    carrierCostBasis: 17.45,
    currency: 'EUR',
    checkoutUse: 'Only after packaging proof; likely quote-first for heavy mixed orders.',
    sourceNote: 'PostNL basistarieven binnenland 2026, online frankering.'
  },
  {
    id: 'postnl-be-standard-home-2kg',
    country: 'BE',
    label: 'PostNL standaardpakket NL naar BE',
    condition: '0-2 kg',
    customerPrice: null,
    carrierCostBasis: 12.25,
    currency: 'EUR',
    checkoutUse: 'Default BE cost basis for light parcel orders.',
    sourceNote: 'PostNL basistarieven buitenland 2026, online frankering naar Belgie.'
  },
  {
    id: 'postnl-be-standard-home-5kg',
    country: 'BE',
    label: 'PostNL standaardpakket NL naar BE',
    condition: '2-5 kg',
    customerPrice: null,
    carrierCostBasis: 19.25,
    currency: 'EUR',
    checkoutUse: 'BE heavier parcel cost basis; review margin before accepting order.',
    sourceNote: 'PostNL basistarieven buitenland 2026, online frankering naar Belgie.'
  },
  {
    id: 'postnl-be-standard-home-10kg',
    country: 'BE',
    label: 'PostNL standaardpakket NL naar BE',
    condition: '5-10 kg',
    customerPrice: null,
    carrierCostBasis: 24.75,
    currency: 'EUR',
    checkoutUse: 'BE heavy parcel cost basis; keep nets and clubpacks quote-first.',
    sourceNote: 'PostNL basistarieven buitenland 2026, online frankering naar Belgie.'
  }
];

export const outboundCustomerRates: ShippingRate[] = [
  {
    id: 'customer-nl-standard-under-130',
    country: 'NL',
    label: 'Nederland standaard verzending',
    condition: 'Orderwaarde lager dan EUR 130',
    customerPrice: 6.95,
    carrierCostBasis: 7.45,
    currency: 'EUR',
    checkoutUse: 'Default customer rate for normal parcel orders under the free-shipping threshold.',
    sourceNote: 'Matches the common niche-webshop tactic and direct pickleball competitor pricing.'
  },
  {
    id: 'customer-nl-standard-free-over-130',
    country: 'NL',
    label: 'Nederland gratis verzending',
    condition: 'Orderwaarde vanaf EUR 130',
    customerPrice: 0,
    carrierCostBasis: 7.45,
    currency: 'EUR',
    checkoutUse: 'Use only when margin check passes and shipment fits normal parcel dimensions.',
    sourceNote: 'Free-shipping threshold copied from the direct niche competitor pattern.'
  },
  {
    id: 'customer-be-standard-under-70',
    country: 'BE',
    label: 'Belgie standaard verzending',
    condition: 'Orderwaarde lager dan EUR 70',
    customerPrice: 12.75,
    carrierCostBasis: 12.25,
    currency: 'EUR',
    checkoutUse: 'Default BE customer rate for light parcel orders under EUR 70.',
    sourceNote: 'Aligned with JOOLA-style NL/BE tiering and PostNL BE cost basis.'
  },
  {
    id: 'customer-be-standard-70-to-100',
    country: 'BE',
    label: 'Belgie gereduceerde verzending',
    condition: 'Orderwaarde vanaf EUR 70 tot EUR 100',
    customerPrice: 6.5,
    carrierCostBasis: 12.25,
    currency: 'EUR',
    checkoutUse: 'Use only when margin check passes; subsidized shipping tier.',
    sourceNote: 'Aligned with JOOLA-style NL/BE tiering.'
  },
  {
    id: 'customer-be-standard-free-over-100',
    country: 'BE',
    label: 'Belgie gratis verzending',
    condition: 'Orderwaarde vanaf EUR 100',
    customerPrice: 0,
    carrierCostBasis: 12.25,
    currency: 'EUR',
    checkoutUse: 'Use only for normal parcel orders after VAT/OSS and margin checks pass.',
    sourceNote: 'Aligned with JOOLA-style NL/BE free-shipping threshold.'
  },
  {
    id: 'customer-bulky-quote-first',
    country: 'NL',
    label: 'Bulky pakket of clubpakket',
    condition: 'Portable nets, court lines, club/school packages or mixed bulky orders',
    customerPrice: null,
    carrierCostBasis: null,
    currency: 'EUR',
    checkoutUse: 'Quote-first only; no automatic checkout rate.',
    sourceNote: 'Matches large-product competitor tactic: quote or surcharge for bulky shipping.'
  }
];

export const returnCostRates: ReturnCostRate[] = [
  {
    id: 'return-nl-letterbox',
    origin: 'NL',
    destination: 'Tilburg NL',
    label: 'Nederland retour brievenbuspakje',
    customerCost: 4.4,
    currency: 'EUR',
    responsibility: 'Consumer pays own return postage after withdrawal unless ProPickleball caused the return.',
    sourceNote: 'PostNL Brievenbuspakje+ online cost basis.'
  },
  {
    id: 'return-nl-standard-parcel',
    origin: 'NL',
    destination: 'Tilburg NL',
    label: 'Nederland retour standaardpakket',
    customerCost: 7.45,
    currency: 'EUR',
    responsibility: 'Consumer pays own return postage after withdrawal unless ProPickleball caused the return.',
    sourceNote: 'PostNL package to home address online cost basis up to 10 kg.'
  },
  {
    id: 'return-nl-heavy-parcel',
    origin: 'NL',
    destination: 'Tilburg NL',
    label: 'Nederland retour zwaar pakket',
    customerCost: 17.45,
    currency: 'EUR',
    responsibility: 'Use only after packaging/weight check; quote-first for bulky returns.',
    sourceNote: 'PostNL package to home address online cost basis 10-23 kg.'
  },
  {
    id: 'return-be-to-nl-starting-rate',
    origin: 'BE',
    destination: 'Tilburg NL',
    label: 'Belgie retour naar Nederland',
    customerCost: 10.9,
    currency: 'EUR',
    responsibility: 'Indicative customer-arranged return starting rate; prepaid BE return labels require carrier-account proof.',
    sourceNote: 'PostNL Belgium says parcels to the Netherlands start from EUR 10.90.'
  },
  {
    id: 'return-bulky-quote',
    origin: 'NL',
    destination: 'Tilburg NL',
    label: 'Bulky/netten/clubpakket retour',
    customerCost: null,
    currency: 'EUR',
    responsibility: 'Quote-first with written instructions before purchase and before return.',
    sourceNote: 'Bulky shipping is not covered by the automatic standard table.'
  }
];

export const publicOutboundRateBullets = outboundCustomerRates.map((rate) => {
  const price = rate.customerPrice === null ? 'op offerte' : rate.customerPrice === 0 ? 'gratis' : `EUR ${rate.customerPrice.toFixed(2)}`;
  return `${rate.label}: ${price} (${rate.condition}).`;
});

export const publicReturnCostBullets = returnCostRates.map((rate) => {
  const price = rate.customerCost === null ? 'op offerte' : `indicatief EUR ${rate.customerCost.toFixed(2)}`;
  return `${rate.label}: ${price}. ${rate.responsibility}`;
});

export const shippingRatesPayload = {
  policy: shippingPolicyDecision,
  carrierCostBasisRates,
  outboundCustomerRates,
  returnCostRates
} as const;

export type ShippingRatesPayload = typeof shippingRatesPayload;
